import React from 'react';

interface FRUpdateFileUploadProps {
  selectedFile: File | null;
  uploadProgress: number;
  statusMessage: string;
  isDragging: boolean;
  isUploading: boolean;
  downloadBlob: Blob | null;
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onDrop: (event: React.DragEvent<HTMLDivElement>) => void;
  onDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave: (event: React.DragEvent<HTMLDivElement>) => void;
  onRemoveFile: () => void;
  onUpload: () => void;
  onDownload: () => void;
  title?: string;
  compact?: boolean;
  hideTitle?: boolean;
  isRevision?: boolean;
}

const FRUpdateFileUpload: React.FC<FRUpdateFileUploadProps> = ({
  selectedFile,
  uploadProgress,
  statusMessage,
  isDragging,
  isUploading,
  downloadBlob,
  onFileChange,
  onDrop,
  onDragOver,
  onDragLeave,
  onRemoveFile,
  onUpload,
  onDownload,
  title = 'İçerik Güncelleme - Dosya Yükle',
  compact = false,
  hideTitle = false,
  isRevision = false
}) => {
  const containerPadding = compact ? 'p-4' : 'p-5';
  const titleClassName = compact ? 'text-sm lg:text-base mb-2' : 'text-xl mb-4';
  const dropPadding = compact ? 'p-3' : 'p-5';
  const iconSize = compact ? 'w-12 h-12' : 'w-14 h-14';
  const iconSvgSize = compact ? 'w-6 h-6' : 'w-7 h-7';
  const labelPadding = compact ? 'px-4 py-2' : 'px-4 py-2';
  const labelText = compact ? 'text-sm' : 'text-sm';
  const buttonPadding = compact ? 'py-2 px-4' : 'py-2.5 px-4';
  const fileBlockPadding = compact ? 'p-2.5' : 'p-3';
  return (
    <>
      <div className={`bg-white dark:bg-gray-800 rounded-2xl shadow-lg border-2 ${downloadBlob ? 'border-solid border-green-300 dark:border-green-700' : 'border-dashed border-gray-300 dark:border-gray-600'} ${containerPadding}`}>
        {!hideTitle && (
          <div className="text-center mb-4">
            <h2 className={`font-bold text-gray-900 dark:text-white ${titleClassName} mb-1`}>
              {title}
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">Word formatında dokümanınızı yükleyin</p>
          </div>
        )}
        
        {/* İşlem Tamamlandı - Dosya Hazır */}
        {downloadBlob && (
          <div className="text-center py-8">
            <div className="flex flex-col items-center space-y-4">
              {/* Dosya İkonu */}
              <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center shadow-md cursor-pointer hover:scale-105 transition-transform border-2 border-green-300 dark:border-green-700" onClick={onDownload}>
                <svg className="w-10 h-10 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
              </div>
              
              {/* Bilgi Mesajı */}
              <div>
                <p className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                  Doküman Hazır!
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Etiketlenmiş dokümanınızı indirmek için tıklayın
                </p>
              </div>

              {/* İndirme Butonu */}
              <button
                onClick={onDownload}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium text-sm transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
                </svg>
                Dokümanı İndir
              </button>
            </div>
          </div>
        )}

        {/* İşlem Devam Ediyor - Spinner */}
        {isUploading && !downloadBlob && (
          <div className="text-center py-8">
            <div className="flex flex-col items-center space-y-4">
              {/* Spinner */}
              <div className="w-20 h-20 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center shadow-md border-2 border-blue-300 dark:border-blue-700">
                <svg className="animate-spin w-10 h-10 text-blue-600 dark:text-blue-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              
              {/* Bilgi Mesajı */}
              <div>
                <p className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                  {isRevision ? 'Revizyon Agentı Çalışıyor...' : 'Doküman Etiketleniyor...'}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Lütfen bekleyin, bu işlem birkaç dakika sürebilir
                </p>
              </div>

              {/* İlerleme Çubuğu */}
              <div className="w-full max-w-md">
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <div className="text-sm font-semibold text-blue-600 dark:text-blue-400 mt-2">
                  {uploadProgress}%
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Dosya Seçme Alanı - Sadece yükleme başlamadıysa göster */}
        {!isUploading && !downloadBlob && (
          <>
            <div
              onDrop={onDrop}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              className={`text-center transition-all duration-300 ${
                isDragging 
                  ? 'bg-blue-50 dark:bg-blue-900/20' 
                  : ''
              }`}
            >
              <div className={`flex flex-col items-center ${compact ? 'space-y-2.5' : 'space-y-3 lg:space-y-4'}`}>
                <div className={`${iconSize} bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center`}>
                  <svg className={`${iconSvgSize} text-blue-600 dark:text-blue-400`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                  </svg>
                </div>
                
                <div>
                  <p className={`${compact ? 'text-base mb-1' : 'text-base mb-1'} font-medium text-gray-900 dark:text-white`}>
                    Dosyanızı buraya sürükleyin veya seçin
                  </p>
                  <p className={`${compact ? 'text-sm' : 'text-xs'} text-gray-600 dark:text-gray-400`}>
                    DOCX dosyaları desteklenir
                  </p>
                </div>
                
                <label className={`bg-blue-600 hover:bg-blue-700 text-white rounded-lg cursor-pointer transition-colors font-medium ${labelPadding} ${labelText}`}>
                  <input type="file" className="hidden" onChange={onFileChange} />
                  Dosya Seç
                </label>
              </div>
            </div>

            {/* Seçilen Dosya Gösterimi */}
            {selectedFile && (
              <div className={`mt-2.5 lg:mt-3 ${fileBlockPadding} bg-white/25 dark:bg-gray-800/25 backdrop-blur-xl rounded-lg lg:rounded-xl border border-white/30 dark:border-white/10`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center space-x-2 lg:space-x-3 min-w-0">
                    <div className={`${compact ? 'w-7 h-7 lg:w-8 lg:h-8' : 'w-8 h-8 lg:w-10 lg:h-10'} flex-shrink-0 bg-green-500/20 dark:bg-green-500/30 backdrop-blur-sm rounded-lg flex items-center justify-center border border-green-500/30`}>
                      <svg className={`${compact ? 'w-3.5 h-3.5 lg:w-4 lg:h-4' : 'w-4 h-4 lg:w-5 lg:h-5'} text-green-600 dark:text-green-400`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                    </div>
                    <div className="min-w-0">
                      <p className={`${compact ? 'text-xs lg:text-sm' : 'text-sm lg:text-base'} font-medium text-gray-900 dark:text-white truncate`}>{selectedFile.name}</p>
                      <p className={`${compact ? 'text-[11px] lg:text-xs' : 'text-xs lg:text-sm'} text-gray-500 dark:text-gray-400`}>
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                  <button 
                    onClick={onRemoveFile}
                    className={`${compact ? 'p-1' : 'p-1.5 lg:p-2'} text-red-500 hover:text-red-700 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex-shrink-0`}
                  >
                    <svg className={`${compact ? 'w-4 h-4' : 'w-4 h-4 lg:w-5 lg:h-5'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {/* Yükle Butonu */}
            <div className="mt-2.5 lg:mt-3">
              <button
                onClick={onUpload}
                disabled={!selectedFile}
                className={`w-full rounded-lg font-medium transition-all duration-300 ${buttonPadding} ${compact ? 'text-xs lg:text-sm' : 'text-sm lg:text-base'} ${
                  !selectedFile
                    ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl'
                }`}
              >
                Dosyayı Yükle
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default FRUpdateFileUpload;

